import { BaryProblem, IBaryProblem } from 'models/problem';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';

export enum ResultType {
  FAILED = 'FAILED',
  SUCCESS = 'SUCCESS',
}

export interface IBaryResult<T> {
  resultType?: ResultType;
  baryProblem?: IBaryProblem;
  unparsedError?: any;
  data?: T | Array<T> | string;

  isOk(): boolean;
}

export class BaryResult<T> implements IBaryResult<T> {
  resultType?: ResultType;
  baryProblem?: IBaryProblem;
  data?: T | Array<T> | string;

  isOk(): boolean {
    return this.resultType ? this.resultType === ResultType.SUCCESS : false;
  }
}

export class BaryResultBuilder<T> {
  private baryResult!: BaryResult<T>;

  constructor() {
    this.reset();
  }

  private static buildProblem(parsedResponse: any): IBaryProblem {
    const baryProblem = new BaryProblem();
    if (baryProblem.baryErrors.length === 0) {
      baryProblem.baryErrors = parsedResponse.errors ? parsedResponse.errors : [];
    }
    if (baryProblem.baryErrors.length === 0) {
      baryProblem.baryErrors = parsedResponse.baryErrors ? parsedResponse.baryErrors : [];
    }

    if (!baryProblem.errorCode) {
      baryProblem.errorCode = parsedResponse.code ? parsedResponse.code : undefined;
    }
    if (!baryProblem.errorCode) {
      baryProblem.errorCode = parsedResponse.errorCode ? parsedResponse.errorCode : undefined;
    }
    if (!baryProblem.type) {
      baryProblem.type = parsedResponse.type ? parsedResponse.type : undefined;
    }
    if (!baryProblem.status) {
      baryProblem.status = parsedResponse.status ? parsedResponse.status : undefined;
    }

    return baryProblem;
  }

  private static isBaryProblem(errorData: any): errorData is IBaryProblem {
    return !!(errorData as IBaryProblem).type;
  }

  private static hasErrors(errorData: any): boolean {
    return !!errorData.errors;
  }

  public reset(): void {
    this.baryResult = new BaryResult();
  }

  public parseResponse(response: HttpResponse<any> | HttpErrorResponse | any): BaryResultBuilder<T> {
    if (response) {
      if (typeof response === 'string') {
        this.parseAsJson(response);
      } else if (response instanceof HttpResponse) {
        this.parseAsHttpResponse(response);
      } else if (response instanceof HttpErrorResponse) {
        this.parseAsHttpErrorResponse(response);
      } else {
        console.error('ResponseGetTemplate is not an instance of HttpResponse|HttpErrorResponse|string, skip parsing.');
      }
    }
    return this;
  }

  public success(check: boolean): BaryResultBuilder<T> {
    if (check) {
      this.ok();
    } else {
      this.ko();
    }
    return this;
  }

  public ok(): BaryResultBuilder<T> {
    this.baryResult.resultType = ResultType.SUCCESS;
    return this;
  }

  public ko(): BaryResultBuilder<T> {
    this.baryResult.resultType = ResultType.FAILED;
    return this;
  }

  public data(data: any): BaryResultBuilder<T> {
    if (data) {
      if (data instanceof Array) {
        if (data.length > 0) {
          this.baryResult.data = data;
        }
      } else {
        this.baryResult.data = data;
      }
    }
    return this;
  }

  public build(): BaryResult<T> {
    return this.baryResult;
  }

  public parseAsHttpResponse(response: HttpResponse<T>): void {
    this.baryResult.data = response.body!;
  }

  private parseAsHttpErrorResponse(response: HttpErrorResponse): void {
    if (BaryResultBuilder.isBaryProblem(response.error)) {
      this.baryResult.baryProblem = response.error;
    } else {
      this.baryResult.data = response.error;
    }
  }

  private parseAsJson(response: string): void {
    if (response.startsWith('{') && response.endsWith('}')) {
      try {
        const parsedResponse = JSON.parse(response);
        this.baryResult.data = parsedResponse;
        if (BaryResultBuilder.isBaryProblem(parsedResponse)) {
          this.baryResult.baryProblem = parsedResponse;
        } else if (BaryResultBuilder.hasErrors(parsedResponse)) {
          this.baryResult.baryProblem = BaryResultBuilder.buildProblem(parsedResponse);
        } else {
          this.baryResult.data = parsedResponse;
        }
      } catch (ex) {
        console.error('cannot parse response !');
      }
    }
  }
}
